import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Search, Sparkles, Shield, Calendar, Star, Menu, X } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import ConcernSearch from '@/components/ConcernSearch';
import Footer from '@/components/Footer';

const LOGO_URL = '/assets/hero/logo.png';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, login, register, googleLogin } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [topReviews, setTopReviews] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchTopReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews/top`);
        setTopReviews(response.data);
      } catch (error) {
        console.error('Error fetching top reviews:', error);
      }
    };
    fetchTopReviews();
  }, []);

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register state
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regName, setRegName] = useState('');
  const [regRole, setRegRole] = useState('patient');

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const userData = await login(loginEmail, loginPassword);
      toast.success('Login successful!');
      setShowAuth(false);
      navigate(userData.role === 'healer' ? '/healer-dashboard' : (userData.role === 'admin' ? '/admin' : '/dashboard'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const userData = await register(regEmail, regPassword, regName, regRole);
      toast.success('Registration successful!');
      setShowAuth(false);
      navigate(userData.role === 'healer' ? '/healer-dashboard' : '/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/browse?search=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate('/browse');
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Ourmend | Community Healers. Cultural Care.</title>
        <meta name="description" content="Find and book healers of color. Doctors, acupuncturists, herbalists, massage therapists, shamans and more. Community Healers. Cultural Care." />
      </Helmet>
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-lg border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <img src={LOGO_URL} alt="Ourmend Logo" className="h-9 w-9 sm:h-12 sm:w-12 object-contain" />
            <div>
              <span className="text-lg sm:text-2xl font-bold" style={{color: '#8B4513'}}>Ourmend</span>
              <p className="text-[10px] sm:text-xs text-stone-600 font-bold">Community Healers. Cultural Care.</p>
            </div>
          </div>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/browse')} className="text-stone-700 hover:text-stone-900" data-testid="nav-browse-btn">
              Browse Healers
            </Button>
            <Button variant="ghost" onClick={() => navigate('/join-as-healer')} className="text-stone-700 hover:text-stone-900" data-testid="nav-join-healer-btn">
              Join As A Healer
            </Button>
            <Button variant="ghost" onClick={() => navigate('/contact')} className="text-stone-700 hover:text-stone-900" data-testid="nav-contact-btn">
              Contact Us
            </Button>
            {user ? (
              <Button onClick={() => navigate(user.role === 'healer' ? '/healer-dashboard' : (user.role === 'admin' ? '/admin' : '/dashboard'))} className="bg-[#8B4513] hover:bg-[#6B3410] text-white" data-testid="nav-dashboard-btn">
                Dashboard
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => navigate('/signup')} className="border-[#8B4513] text-[#8B4513] hover:bg-amber-50" data-testid="nav-signup-btn">
                  Sign Up
                </Button>
                <Button onClick={() => setShowAuth(true)} className="bg-[#8B4513] hover:bg-[#6B3410] text-white" data-testid="nav-signin-btn">
                  Sign In
                </Button>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X className="w-6 h-6 text-stone-700" /> : <Menu className="w-6 h-6 text-stone-700" />}
          </button>
        </div>

        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-stone-200 px-4 py-4 space-y-3">
            <Button variant="ghost" className="w-full justify-start text-stone-700" onClick={() => { navigate('/browse'); setMobileMenuOpen(false); }}>
              Browse Healers
            </Button>
            <Button variant="ghost" className="w-full justify-start text-stone-700" onClick={() => { navigate('/join-as-healer'); setMobileMenuOpen(false); }}>
              Join As A Healer
            </Button>
            <Button variant="ghost" className="w-full justify-start text-stone-700" onClick={() => { navigate('/contact'); setMobileMenuOpen(false); }} data-testid="mobile-nav-contact-btn">
              Contact Us
            </Button>
            {user ? (
              <Button className="w-full bg-[#8B4513] hover:bg-[#6B3410] text-white" onClick={() => { navigate(user.role === 'healer' ? '/healer-dashboard' : (user.role === 'admin' ? '/admin' : '/dashboard')); setMobileMenuOpen(false); }}>
                Dashboard
              </Button>
            ) : (
              <>
                <Button variant="outline" className="w-full border-[#8B4513] text-[#8B4513]" onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }} data-testid="mobile-nav-signup-btn">
                  Sign Up
                </Button>
                <Button className="w-full bg-[#8B4513] hover:bg-[#6B3410] text-white" onClick={() => { setShowAuth(true); setMobileMenuOpen(false); }}>
                  Sign In
                </Button>
              </>
            )}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-20 px-4 sm:px-6 bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center space-y-6 sm:space-y-8 fade-in">
            {/* Heading, Logo, and Images Below */}
            <div className="flex flex-col items-center justify-center gap-4 sm:gap-6">
              <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold leading-tight text-center" style={{color: '#3E2723'}}>
                Find Your Path to
                <span className="block mt-2" style={{color: '#8B4513'}}>Holistic Healing</span>
              </h1>

              {/* Clear subheading */}
              <p className="text-base sm:text-xl lg:text-2xl text-stone-700 max-w-3xl mx-auto text-center font-medium leading-relaxed" data-testid="hero-subheading">
                Connecting people of color with trusted healers who understand their lived experience.
              </p>
              
              {/* Center Logo */}
              <img 
                src={LOGO_URL} 
                alt="Ourmend Logo" 
                className="h-44 w-44 sm:h-64 sm:w-64 md:h-80 md:w-80 object-contain" 
              />
              
              {/* Images Below Logo */}
              <div className="grid grid-cols-2 md:grid-cols-4 items-center justify-center gap-3 sm:gap-4">
                <img 
                  src="/assets/hero/healing-touch.png" 
                  alt="Healing Touch" 
                  className="w-full h-36 sm:h-48 md:h-64 md:w-64 object-cover rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/hero/crystal-healing.jpg" 
                  alt="Crystal Healing Therapy" 
                  className="w-full h-36 sm:h-48 md:h-64 md:w-64 object-cover rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/hero/medical-care.jpg" 
                  alt="Professional Medical Care" 
                  className="w-full h-36 sm:h-48 md:h-64 md:w-64 object-cover rounded-lg shadow-lg"
                />
                <img 
                  src="/assets/hero/massage.jpg" 
                  alt="Massage Therapy" 
                  className="w-full h-36 sm:h-48 md:h-64 md:w-64 object-cover rounded-lg shadow-lg"
                />
              </div>
              
              {/* Updated Bold Text */}
              <p className="text-sm sm:text-lg lg:text-xl font-bold text-stone-700 max-w-4xl mx-auto text-center mt-4 sm:mt-6">
                People of color deserve care rooted in cultural understanding. Connect instantly with trusted healers of color—from medical doctors and therapists to spiritual practitioners—who recognize your lived experience and treat you as a whole person.
              </p>
            </div>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mt-8 sm:mt-12">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 glass-card p-2 sm:p-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-stone-400 w-5 h-5" />
                  <Input
                    placeholder="Search by healer type, specialty, or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-12 h-12 sm:h-14 text-sm sm:text-base border-0 bg-white shadow-sm"
                    data-testid="hero-search-input"
                  />
                </div>
                <Button
                  onClick={handleSearch}
                  className="h-12 sm:h-14 px-6 sm:px-8 bg-[#8B4513] hover:bg-[#6B3410] text-white text-sm sm:text-base font-medium"
                  data-testid="hero-search-btn"
                >
                  Search
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-4xl font-bold text-center mb-8 sm:mb-16" style={{color: '#3E2723'}}>Why Choose Ourmend</h2>
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            <div className="glass-card p-8 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-6">
                <Sparkles className="w-7 h-7" style={{color: '#8B4513'}} />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{color: '#3E2723'}}>Diverse Practitioners</h3>
              <p className="text-stone-600 leading-relaxed">
                Access a wide range of healing modalities from acupuncture to energy healing, all in one place.
              </p>
            </div>

            <div className="glass-card p-8 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-7 h-7" style={{color: '#8B4513'}} />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{color: '#3E2723'}}>Verified Healers</h3>
              <p className="text-stone-600 leading-relaxed">
                All practitioners are verified with credentials and years of experience to ensure quality care.
              </p>
            </div>

            <div className="glass-card p-8 hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-yellow-100 rounded-xl flex items-center justify-center mb-6">
                <Calendar className="w-7 h-7" style={{color: '#8B4513'}} />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{color: '#3E2723'}}>Easy Booking</h3>
              <p className="text-stone-600 leading-relaxed">
                View real-time availability and book appointments instantly with secure payment processing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Symptom / Concern Search (non-AI, rules-based matching) */}
      <ConcernSearch />

      {/* Testimonials */}
      {topReviews.length > 0 && (
        <section className="py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="testimonials-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-4xl font-bold text-center mb-3 sm:mb-4" style={{color: '#3E2723'}}>What Our Community Says</h2>
            <p className="text-sm sm:text-base text-center text-stone-600 mb-8 sm:mb-12 max-w-2xl mx-auto">Real experiences from people who found healing through cultural care</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {topReviews.map((review) => (
                <Card key={review.id} className="p-6 bg-white hover:shadow-lg transition-shadow" data-testid={`testimonial-${review.id}`}>
                  <div className="flex mb-3">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                    ))}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-4 line-clamp-4">"{review.text}"</p>
                  {review.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {review.tags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="border-t pt-3 mt-auto">
                    <p className="font-semibold text-sm text-gray-900">{review.patient_name}</p>
                    <p className="text-xs text-stone-500">on {review.healer_name} &middot; {review.healer_type}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6" style={{background: '#8B4513'}}>
        <div className="max-w-4xl mx-auto text-center space-y-4 sm:space-y-6 text-white">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold">Ready to Begin Your Healing Journey?</h2>
          <p className="text-base sm:text-xl opacity-90">
            Join thousands finding wellness through culturally connected care.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/browse')}
            className="bg-white hover:bg-stone-100 h-12 sm:h-14 px-8 sm:px-10 text-base sm:text-lg font-semibold mt-6 sm:mt-8"
            style={{color: '#8B4513'}}
            data-testid="cta-explore-btn"
          >
            Explore Healers
          </Button>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="sm:max-w-md" data-testid="auth-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">Welcome</DialogTitle>
            <DialogDescription>
              Sign in to book appointments with healers
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="login" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="space-y-4 mt-6">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-[#8B4513] hover:bg-[#6B3410] text-white" data-testid="login-submit-btn">
                  Login
                </Button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or</span>
                </div>
              </div>

              <Button
                onClick={googleLogin}
                variant="outline"
                className="w-full"
                data-testid="google-login-btn"
              >
                Continue with Google
              </Button>
            </TabsContent>

            <TabsContent value="register" className="space-y-4 mt-6">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="reg-name">Full Name</Label>
                  <Input
                    id="reg-name"
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    required
                    data-testid="register-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="reg-email">Email</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    required
                    data-testid="register-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="reg-password">Password</Label>
                  <Input
                    id="reg-password"
                    type="password"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    required
                    data-testid="register-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="reg-role">I am a</Label>
                  <select
                    id="reg-role"
                    value={regRole}
                    onChange={(e) => setRegRole(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    data-testid="register-role-select"
                  >
                    <option value="patient">Patient</option>
                    <option value="healer">Healer</option>
                  </select>
                </div>
                <Button type="submit" className="w-full bg-[#8B4513] hover:bg-[#6B3410] text-white" data-testid="register-submit-btn">
                  Register
                </Button>
              </form>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or</span>
                </div>
              </div>

              <Button
                onClick={googleLogin}
                variant="outline"
                className="w-full"
                data-testid="google-register-btn"
              >
                Continue with Google
              </Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;